<template>
  <AppShell>
    <div class="page-head">
      <div>
        <h1>行业资讯</h1>
        <p class="sub">算力选型、价格与招标相关的站内内容，方便检索与抓取。</p>
      </div>
    </div>

    <section class="list-wrap">
      <form class="search" @submit.prevent="onSearch">
        <input v-model="keyword" placeholder="搜索标题或标签" aria-label="搜索文章" />
        <button type="submit" class="ghost">搜索</button>
      </form>

      <p v-if="tag" class="filter-tip">
        标签：{{ tag }}
        <button type="button" class="ghost sm" @click="clearTag">清除</button>
      </p>

      <p v-if="error" class="error">{{ error }}</p>
      <p v-else-if="loading && !articles.length" class="muted">加载中…</p>
      <p v-else-if="!loading && !articles.length" class="muted">
        暂无已发布资讯。
        <router-link class="linkish" to="/">去查参数</router-link>
      </p>

      <button
        v-for="item in articles"
        :key="item.id"
        type="button"
        class="row"
        @click="openArticle(item.slug)"
      >
        <strong>{{ item.title }}</strong>
        <span v-if="item.summary" class="summary">{{ item.summary }}</span>
        <span class="meta">
          <template v-if="item.published_at">{{ formatTime(item.published_at) }}</template>
          <template v-if="item.cards && item.cards.length"> · 关联 {{ item.cards.length }} 款卡</template>
          <template v-if="tagList(item.tags).length">
            ·
            <em
              v-for="t in tagList(item.tags).slice(0, 4)"
              :key="t"
              class="tag"
              @click.stop="goTag(t)"
            >{{ t }}</em>
          </template>
        </span>
      </button>

      <ListPager
        :page="paging.page"
        :pages="paging.pages"
        :total="paging.total"
        @change="goPage"
      />
    </section>
  </AppShell>
</template>

<script>
import AppShell from '../layouts/AppShell.vue'
import ListPager from '../components/ListPager.vue'
import { listArticles } from '../api/platformService'
import { clearPageMeta, setPageMeta } from '../utils/pageMeta'

export default {
  name: 'ArticlesWorkspace',
  components: { AppShell, ListPager },
  data() {
    return {
      articles: [],
      keyword: '',
      tag: '',
      paging: { page: 1, pages: 1, total: 0 },
      loading: false,
      error: ''
    }
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler(query) {
        this.tag = (query.tag || '').trim()
        this.keyword = (query.q || '').trim()
        const page = Math.max(1, parseInt(query.page, 10) || 1)
        this.load(page)
      }
    }
  },
  mounted() {
    setPageMeta({
      title: '行业资讯 · 算力百科',
      description: '算力百科行业资讯：选型、价格与招标相关内容。',
      canonical: `${window.location.origin}/articles`,
      ogType: 'website',
      ogImage: `${window.location.origin}/icon.png`
    })
  },
  beforeUnmount() {
    clearPageMeta()
  },
  methods: {
    formatTime(iso) {
      if (!iso) return ''
      return String(iso).replace('T', ' ').slice(0, 16)
    },
    tagList(raw) {
      return String(raw || '')
        .split(/[,，]/)
        .map((t) => t.trim())
        .filter(Boolean)
    },
    queryBase() {
      return {
        ...(this.keyword.trim() ? { q: this.keyword.trim() } : {}),
        ...(this.tag ? { tag: this.tag } : {})
      }
    },
    onSearch() {
      this.$router.replace({ path: '/articles', query: this.queryBase() })
    },
    clearTag() {
      const q = this.keyword.trim()
      this.$router.replace({ path: '/articles', query: q ? { q } : {} })
    },
    goTag(tag) {
      this.$router.push({ path: '/articles', query: { tag } })
    },
    goPage(page) {
      this.$router.replace({
        path: '/articles',
        query: { ...this.queryBase(), ...(page > 1 ? { page: String(page) } : {}) }
      })
    },
    openArticle(slug) {
      this.$router.push(`/articles/${encodeURIComponent(slug)}`)
    },
    async load(page = 1) {
      this.loading = true
      this.error = ''
      try {
        const data = await listArticles({
          q: this.keyword.trim() || undefined,
          tag: this.tag || undefined,
          page,
          per_page: 20
        })
        this.articles = data.articles || []
        const total = data.paging?.total || 0
        const per = data.paging?.per_page || 20
        this.paging = {
          page: data.paging?.page || page,
          pages: Math.max(1, Math.ceil(total / per) || 1),
          total
        }
      } catch (err) {
        this.error = err.response?.data?.error || '加载失败'
        this.articles = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
h1 { font-size: 34px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.page-head { margin-bottom: 8px; }
.sub { color: #86909c; margin: 0 0 12px; line-height: 1.6; font-size: 15px; }
.list-wrap { max-width: 860px; }
.search { display: flex; gap: 8px; margin-bottom: 12px; max-width: 520px; }
.search input {
  flex: 1; border: 1px solid #e5e6eb; border-radius: 12px; padding: 8px 12px; font: inherit;
}
.ghost {
  border: 1px solid #e5e6eb; background: #fff; color: #1d2129; border-radius: 12px;
  padding: 8px 14px; font: inherit; cursor: pointer;
}
.ghost:hover { border-color: #165dff; color: #165dff; }
.ghost.sm { padding: 2px 8px; border-radius: 8px; font-size: 12px; margin-left: 8px; }
.filter-tip { margin: 0 0 12px; font-size: 13px; color: #4e5969; }
.row {
  display: grid; gap: 4px; width: 100%; text-align: left; cursor: pointer;
  background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 14px 16px;
  margin-bottom: 10px; font: inherit; color: inherit;
}
.row:hover { border-color: #165dff; }
.row strong { font-size: 17px; font-weight: 650; letter-spacing: -0.02em; }
.summary {
  color: #4e5969; font-size: 14px; line-height: 1.6;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.meta { color: #86909c; font-size: 12px; line-height: 1.5; }
.tag {
  display: inline-block; margin-right: 4px; padding: 0 6px; border-radius: 999px;
  background: #f2f3f5; color: #4e5969; font-style: normal; cursor: pointer;
}
.tag:hover { background: #e8f3ff; color: #165dff; }
.linkish { color: #165dff; text-decoration: none; margin-left: 6px; }
.linkish:hover { text-decoration: underline; }
.error { color: #d3382c; }
.muted { color: #86909c; line-height: 1.6; }
</style>
